/**
 * Platform metadata tool — TOP_TOOLBAR.
 *
 * Phase 13 P1 (June 2026)
 *
 * Display name + description fields, scene_type pill, difficulty
 * picker. Binds to every scene unconditionally. The Phase-12
 * faceted picks (quiz_format / quiz_subject) live in their own
 * P3 tools so this stays scene-type-agnostic.
 *
 * Implementation note
 * -------------------
 *
 * This is the canonical pattern every P1 tool follows:
 *
 *   1. Read scene + capabilities from `useDesigner()`
 *   2. Mutate via `setMeta` / `setDomainMetadata` (NOT direct
 *      sceneStore.setState) so the recorded undo unit is the
 *      logical edit, not the raw store mutation.
 *   3. Render only when there's a scene to edit (`<NoScene/>`
 *      placeholder when scene is null).
 */
import React from 'react';

import { DesignerTool } from '../DesignerTool';
import { useDesigner } from '../DesignerContext';
import { always } from '../predicates';

function MetadataForm() {
  const { scene, setMeta } = useDesigner();
  if (!scene) return null;

  return (
    <div className="designer-tool" data-tool-id="platform.metadata_form">
      <div className="designer-tool__row">
        <label className="designer-tool__label">Display name</label>
        <input
          type="text"
          className="designer-tool__input"
          value={scene.display_name || ''}
          aria-label="Scene display name"
          onChange={(e) => setMeta({ display_name: e.target.value })}
        />
      </div>
      <div className="designer-tool__row">
        <label className="designer-tool__label">Description</label>
        <input
          type="text"
          className="designer-tool__input"
          value={scene.description || ''}
          aria-label="Scene description"
          onChange={(e) => setMeta({ description: e.target.value })}
        />
      </div>
      <div className="designer-tool__row">
        <span className="designer-tool__chip" data-chip="scene-type">
          {scene.scene_type}
        </span>
      </div>
      <div className="designer-tool__row">
        <label className="designer-tool__label">Access Tier</label>
        <select
          className="designer-tool__input"
          value={scene.access_tier || 'paid'}
          aria-label="Access tier"
          onChange={(e) => setMeta({ access_tier: e.target.value })}
        >
          <option value="free">Free</option>
          <option value="paid">Paid</option>
        </select>
      </div>
      <div className="designer-tool__row">
        <label className="designer-tool__label">Content License</label>
        <select
          className="designer-tool__input"
          value={scene.content_license || ''}
          aria-label="Content license"
          onChange={(e) => setMeta({ content_license: e.target.value || null })}
        >
          <option value="">Original</option>
          <option value="cc_by">CC BY</option>
          <option value="cc_by_nc_sa">CC BY-NC-SA</option>
          <option value="youtube_embed">YouTube Embed</option>
          <option value="external_link">External Link</option>
        </select>
      </div>
      {scene.content_license && ['cc_by_nc_sa', 'youtube_embed'].includes(scene.content_license) && scene.access_tier !== 'free' && (
        <div className="designer-tool__row" style={{ background: '#fef3c7', borderRadius: 4, padding: '6px 8px', fontSize: '0.75rem', color: '#92400e' }}>
          NC-licensed content must be in the free tier to comply with licensing terms.
        </div>
      )}
    </div>
  );
}

export const metadataForm = new DesignerTool({
  id:         'platform.metadata_form',
  label:      'Metadata',
  slot:       'RIGHT_INSP',
  category:   'metadata',
  applies_to: always,                // every scene
  Component:  MetadataForm,
  weight:     10,                    // appears at top of right inspector
  keybinding: 'mod+i',
  helpText:   'Edit the scene\'s display name, description, and scene type.',
});

export default metadataForm;
